import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

export const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Primary buttons are Ink (brand style guide); violet is kept for the Unfold action.
        default:
          "bg-foreground text-background shadow-sm hover:bg-foreground/90 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
        unfold:
          "bg-primary text-primary-foreground rounded-full shadow-[0_6px_10px_rgba(110,83,172,0.45)] hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        outline: "border border-foreground/12 bg-white hover:bg-muted/60 text-foreground",
        ghost: "hover:bg-muted hover:text-foreground text-muted-foreground",
        "ghost-primary": "hover:bg-primary/10 text-primary hover:text-primary",
        pill: "bg-foreground text-background hover:bg-foreground/90 rounded-full px-6",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-14 rounded-xl px-8 text-lg",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      {...props}
    />
  ),
)
Button.displayName = "Button"

export { Button }
